import {
  FC,
  ComponentType,
  Fragment,
  useRef,
  useCallback,
  useState,
  useEffect,
} from 'react';

import ErrorMessage from '@components/ErrorMessage';
import { Row } from '@components/Grid';
import Dropdown from '@components/Dropdown';
import Flag, { FlagProps } from '@components/Flag';
import {
  PhoneNumber,
  PhonNumberProps,
} from '@components/ui/form/FormattedInputs';
import { Turkey, CountryParams } from '@config/countries';
import { SizeUnit } from '@utils/types';
import { useSizes, useHeight } from '@components/utils/hooks/useSize';

import { Code, Container, Containers } from './styled';

export type { InputApi as PhoneApi } from '@components/Input';

const COUNTRIES = [Turkey];

export type Props = {
  onChange?: (phone: string) => void;
  onCountryChange?: (country: CountryParams) => void;
  disabled?: boolean;
  initialValue?: string;
  value?: string;
  placeholder?: string;
  getPhoneApi?: PhonNumberProps['getInputAPI'];
  children?: React.ReactNode;
  updateInitialValue?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  showSuccessModal?: boolean;
  isLetUsCallYou?: boolean;
};

const useResponsiveMarginLeft = () =>
  `${useSizes({ large: 10, small: 5 })}px` as SizeUnit;

const useFixMarginLeft = () => `10px` as SizeUnit;
const hooks = {
  useResponsiveMarginLeft,
  useFixMarginLeft,
};
const rendererFactory: (
  Wrapper: ComponentType<
    React.PropsWithChildren<
      React.PropsWithChildren<
        React.PropsWithChildren<React.PropsWithChildren<unknown>>
      >
    >
  >,
  hook: keyof typeof hooks
) => FC<
  React.PropsWithChildren<
    React.PropsWithChildren<
      React.PropsWithChildren<React.PropsWithChildren<FlagProps>>
    >
  >
> = (Wrapper, hook) => (props) => {
  const marginLeft = hooks[hook]();
  return (
    <Wrapper>
      <Flag {...props} />
      <Code marginLeft={marginLeft}>+{props.prefix}</Code>
    </Wrapper>
  );
};

const Label = rendererFactory(Containers, 'useResponsiveMarginLeft');
const Choice = rendererFactory(Fragment, 'useFixMarginLeft');
const useDropdownHeight = () => `${useHeight()}px` as SizeUnit;

const ResponsivePaddings = { large: 15, small: 5 };

export const parsePhoneNumber = (
  phone: string,
  prefix: number = Turkey.prefix
) => `+${prefix}${phone.replace(/[()\s]/gi, '')}`;

const PhoneInput = ({
  onChange,
  onCountryChange,
  disabled,
  initialValue = '',
  value = '',
  placeholder = 'Cep Telefonu*',
  getPhoneApi,
  children,
  updateInitialValue = false,
  hasError,
  errorMessage,
  showSuccessModal,
  isLetUsCallYou,
}: Props) => {
  const height = useDropdownHeight();
  const [countryCode, setCountryCode] = useState<number>(Turkey.prefix);

  const nativeInputRef = useRef<HTMLInputElement>(
    undefined as unknown as HTMLInputElement
  );

  const memoizedGetInputApiRef = useCallback<NonNullable<Props['getPhoneApi']>>(
    (api, niRef) => {
      getPhoneApi?.(
        {
          reset: () => {
            if (updateInitialValue) {
              api.reset();
              if (niRef) {
                niRef.current.value = '';
              }
            }
          },
          applyValue: (value) => {
            api.applyValue(value);
            if (niRef) {
              niRef.current.value = value;
            }
          },
        },
        niRef
      );
    },
    [getPhoneApi, updateInitialValue]
  );

  const onCountryDropdownChanged = useCallback((country) => {
    setCountryCode(+Number(country.prefix));
    onCountryChange?.(country as CountryParams);
  }, []);

  const onPhoneInputChanged = useCallback(
    (phone) => {
      onChange?.(parsePhoneNumber(phone, countryCode));
    },
    [countryCode, onChange]
  );

  useEffect(() => {
    if (showSuccessModal) {
      nativeInputRef.current.value = '';
      setCountryCode(Turkey.prefix);
      onChange?.('');
      onCountryChange?.(Turkey);
    }
  }, [showSuccessModal, onChange, onCountryChange]);

  return (
    <Row>
      <Container
        data-error={hasError}
        xs={1.5}
        style={{ paddingRight: '2px' }}
        md={isLetUsCallYou ? 1.8 : 3}
      >
        <Dropdown
          phoneError={hasError}
          items={COUNTRIES}
          renderLabel={Label}
          renderOption={Choice}
          getKey={(x) => x?.displayName ?? ''}
          defaultSelectedItem={Turkey}
          height={height}
          paddings={ResponsivePaddings}
          showOptionsWhenNumberItemsExceed={1}
          disabled
          hideIcon
          onChange={onCountryDropdownChanged}
        />
      </Container>
      <Container data-error={hasError} xs={2.5} md={isLetUsCallYou ? 6.2 : 5}>
        <PhoneNumber
          name='__phn__nm' // disable autocomplete by giving value that the browser doesn’t recognise.
          placeholder={placeholder}
          type='text'
          onChange={onPhoneInputChanged}
          disabled={disabled}
          forwardDefaultValue={initialValue}
          getInputAPI={memoizedGetInputApiRef}
          nativeInputRef={nativeInputRef}
          defaultValue={value}
          key={updateInitialValue as unknown as string}
        />
      </Container>
      {hasError ? (
        <div style={{ marginLeft: '10px', marginTop: '10px' }}>
          <ErrorMessage message={errorMessage} showIcon={false} />
        </div>
      ) : null}
      {children}
    </Row>
  );
};

export default PhoneInput;
